import { useState, useMemo } from 'react'
import {
  Block,
  BlockTitle,
  List,
  ListItem,
  Button,
  Segmented,
  SegmentedButton,
  Searchbar,
  Sheet,
  Toolbar,
  ToolbarPane,
  Link,
  Dialog,
  DialogButton,
} from 'konsta/react'
import {
  X,
  User,
  Shield,
  Crown,
  Ban,
  UserCheck,
  Calendar,
  Gamepad2,
  ArrowDownToLine,
  ArrowUpFromLine,
  Wallet,
  Clock,
} from 'lucide-react'
import { formatCurrency, cn } from '@/lib/utils'

type UserStatus = 'active' | 'banned' | 'vip'
type FilterTab = '全部' | '活躍' | '停權' | 'VIP'

interface MockUser {
  id: string
  name: string
  telegramId: string
  balance: number
  status: UserStatus
  role: 'player' | 'agent'
  registered: string
  gamesPlayed: number
  totalDeposit: number
  totalWithdraw: number
}

const mockUsers: MockUser[] = [
  { id: '1', name: '王大明', telegramId: '@daming_wang', balance: 125000, status: 'active', role: 'agent', registered: '2024-01-15', gamesPlayed: 128, totalDeposit: 500000, totalWithdraw: 375000 },
  { id: '2', name: 'David Chen', telegramId: '@david_c', balance: 89000, status: 'active', role: 'player', registered: '2024-02-20', gamesPlayed: 85, totalDeposit: 200000, totalWithdraw: 111000 },
  { id: '3', name: 'Sarah Lin', telegramId: '@sarah_lin', balance: 156000, status: 'active', role: 'agent', registered: '2024-01-10', gamesPlayed: 256, totalDeposit: 800000, totalWithdraw: 644000 },
  { id: '4', name: 'Mike Wang', telegramId: '@mike_w', balance: 67000, status: 'vip', role: 'player', registered: '2023-12-01', gamesPlayed: 512, totalDeposit: 1500000, totalWithdraw: 1433000 },
  { id: '5', name: '小美', telegramId: '@xiao_mei', balance: 203000, status: 'active', role: 'player', registered: '2024-03-05', gamesPlayed: 64, totalDeposit: 300000, totalWithdraw: 97000 },
  { id: '6', name: 'Alex Kim', telegramId: '@alex_k', balance: 0, status: 'banned', role: 'player', registered: '2024-01-25', gamesPlayed: 45, totalDeposit: 100000, totalWithdraw: 100000 },
  { id: '7', name: '張三', telegramId: '@zhang_san', balance: 45000, status: 'active', role: 'player', registered: '2024-04-10', gamesPlayed: 22, totalDeposit: 80000, totalWithdraw: 35000 },
  { id: '8', name: 'Lucy Wu', telegramId: '@lucy_wu', balance: 320000, status: 'vip', role: 'agent', registered: '2023-11-15', gamesPlayed: 890, totalDeposit: 2000000, totalWithdraw: 1680000 },
]

const statusConfig: Record<UserStatus, { label: string; color: string; bg: string }> = {
  active: { label: '活躍', color: '#34C759', bg: 'rgba(52, 199, 89, 0.1)' },
  banned: { label: '停權', color: '#FF3B30', bg: 'rgba(255, 59, 48, 0.1)' },
  vip: { label: 'VIP', color: '#AF52DE', bg: 'rgba(175, 82, 222, 0.1)' },
}

const filterTabs: FilterTab[] = ['全部', '活躍', '停權', 'VIP']

const filterToStatus: Record<FilterTab, UserStatus | null> = {
  '全部': null,
  '活躍': 'active',
  '停權': 'banned',
  'VIP': 'vip',
}

export function AdminUsersScreen() {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilter, setActiveFilter] = useState<FilterTab>('全部')
  const [selectedUser, setSelectedUser] = useState<MockUser | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [banDialogOpen, setBanDialogOpen] = useState(false)

  const filteredUsers = useMemo(() => {
    let users = mockUsers

    // Apply status filter
    const statusFilter = filterToStatus[activeFilter]
    if (statusFilter) {
      users = users.filter((u) => u.status === statusFilter)
    }

    // Apply search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      users = users.filter(
        (u) =>
          u.name.toLowerCase().includes(q) ||
          u.telegramId.toLowerCase().includes(q)
      )
    }

    return users
  }, [searchQuery, activeFilter])

  const handleUserClick = (user: MockUser) => {
    setSelectedUser(user)
    setSheetOpen(true)
  }

  const handleBanToggle = () => {
    setBanDialogOpen(true)
  }

  const handleBanConfirm = () => {
    setBanDialogOpen(false)
    setSheetOpen(false)
    setSelectedUser(null)
  }

  const getUserIcon = (user: MockUser) => {
    if (user.status === 'vip') return Crown
    if (user.role === 'agent') return Shield
    return User
  }

  const getUserIconColor = (user: MockUser) => {
    if (user.status === 'banned') return '#FF3B30'
    if (user.status === 'vip') return '#AF52DE'
    if (user.role === 'agent') return '#007AFF'
    return '#8E8E93'
  }

  return (
    <div className="pb-4">
      {/* Search */}
      <Block className="!mt-2 !mb-0 !px-4">
        <Searchbar
          value={searchQuery}
          onInput={(e: React.FormEvent<HTMLInputElement>) =>
            setSearchQuery((e.target as HTMLInputElement).value)
          }
          onClear={() => setSearchQuery('')}
          placeholder="搜尋用戶名稱或 Telegram ID"
        />
      </Block>

      {/* Filter Tabs */}
      <Block className="!mt-2 !mb-0">
        <Segmented strong rounded>
          {filterTabs.map((tab) => (
            <SegmentedButton
              key={tab}
              active={activeFilter === tab}
              onClick={() => setActiveFilter(tab)}
            >
              {tab}
            </SegmentedButton>
          ))}
        </Segmented>
      </Block>

      {/* User Count */}
      <BlockTitle>
        用戶列表
        <span className="text-[#8E8E93] text-sm font-normal ml-2">
          ({filteredUsers.length})
        </span>
      </BlockTitle>

      {/* User List */}
      <List strongIos insetIos>
        {filteredUsers.length === 0 ? (
          <ListItem title="找不到符合的用戶" className="text-center" />
        ) : (
          filteredUsers.map((user) => {
            const Icon = getUserIcon(user)
            const iconColor = getUserIconColor(user)
            const status = statusConfig[user.status]
            return (
              <ListItem
                key={user.id}
                link
                chevronIos
                onClick={() => handleUserClick(user)}
                title={
                  <span
                    className={cn(
                      'font-medium',
                      user.status === 'banned' && 'line-through text-[#8E8E93]'
                    )}
                  >
                    {user.name}
                  </span>
                }
                subtitle={
                  <span className="text-[#8E8E93] text-xs">
                    {user.telegramId}
                  </span>
                }
                after={
                  <div className="flex flex-col items-end gap-1">
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{
                        color: status.color,
                        backgroundColor: status.bg,
                      }}
                    >
                      {status.label}
                    </span>
                    <span className="text-xs text-[#8E8E93]">
                      {formatCurrency(user.balance)}
                    </span>
                  </div>
                }
                media={
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${iconColor}15` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: iconColor }} />
                  </div>
                }
              />
            )
          })
        )}
      </List>

      {/* User Detail Sheet */}
      <Sheet
        className="pb-safe"
        opened={sheetOpen}
        onBackdropClick={() => setSheetOpen(false)}
        style={{ height: 'auto', maxHeight: '85vh' }}
      >
        <Toolbar top className="justify-between pt-4">
          <ToolbarPane>
            <span className="font-semibold text-base pl-2">用戶詳情</span>
          </ToolbarPane>
          <ToolbarPane>
            <Link iconOnly onClick={() => setSheetOpen(false)}>
              <X className="w-5 h-5" />
            </Link>
          </ToolbarPane>
        </Toolbar>

        {selectedUser && (
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 60px)' }}>
            {/* Profile Header */}
            <Block className="!mt-2">
              <div className="flex items-center gap-4">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center"
                  style={{
                    backgroundColor: `${getUserIconColor(selectedUser)}15`,
                  }}
                >
                  {(() => {
                    const Icon = getUserIcon(selectedUser)
                    return (
                      <Icon
                        className="w-8 h-8"
                        style={{ color: getUserIconColor(selectedUser) }}
                      />
                    )
                  })()}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-black">
                    {selectedUser.name}
                  </h3>
                  <p className="text-sm text-[#8E8E93]">
                    {selectedUser.telegramId}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{
                        color: statusConfig[selectedUser.status].color,
                        backgroundColor: statusConfig[selectedUser.status].bg,
                      }}
                    >
                      {statusConfig[selectedUser.status].label}
                    </span>
                    {selectedUser.role === 'agent' && (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#007AFF]/10 text-[#007AFF]">
                        代理
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Block>

            {/* Financial Overview */}
            <BlockTitle>財務概覽</BlockTitle>
            <Block className="!px-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-[#F2F2F7] p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Wallet className="w-3.5 h-3.5 text-[#007AFF]" />
                    <span className="text-xs text-[#8E8E93]">餘額</span>
                  </div>
                  <p className="text-lg font-bold text-black">
                    {formatCurrency(selectedUser.balance)}
                  </p>
                </div>
                <div className="rounded-xl bg-[#F2F2F7] p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Gamepad2 className="w-3.5 h-3.5 text-[#34C759]" />
                    <span className="text-xs text-[#8E8E93]">遊戲場次</span>
                  </div>
                  <p className="text-lg font-bold text-black">
                    {selectedUser.gamesPlayed}
                  </p>
                </div>
                <div className="rounded-xl bg-[#F2F2F7] p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <ArrowDownToLine className="w-3.5 h-3.5 text-[#34C759]" />
                    <span className="text-xs text-[#8E8E93]">總存款</span>
                  </div>
                  <p className="text-lg font-bold text-[#34C759]">
                    {formatCurrency(selectedUser.totalDeposit)}
                  </p>
                </div>
                <div className="rounded-xl bg-[#F2F2F7] p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <ArrowUpFromLine className="w-3.5 h-3.5 text-[#FF3B30]" />
                    <span className="text-xs text-[#8E8E93]">總提款</span>
                  </div>
                  <p className="text-lg font-bold text-[#FF3B30]">
                    {formatCurrency(selectedUser.totalWithdraw)}
                  </p>
                </div>
              </div>
            </Block>

            {/* Account Info */}
            <BlockTitle>帳戶資訊</BlockTitle>
            <List strongIos insetIos>
              <ListItem
                title="註冊日期"
                after={selectedUser.registered}
                media={<Calendar className="w-5 h-5 text-[#8E8E93]" />}
              />
              <ListItem
                title="帳戶角色"
                after={selectedUser.role === 'agent' ? '代理' : '玩家'}
                media={<Shield className="w-5 h-5 text-[#8E8E93]" />}
              />
              <ListItem
                title="最後活躍"
                after="2 小時前"
                media={<Clock className="w-5 h-5 text-[#8E8E93]" />}
              />
            </List>

            {/* Recent Activity */}
            <BlockTitle>近期活動</BlockTitle>
            <List strongIos insetIos>
              <ListItem
                title="加入高額桌 VIP"
                subtitle="30 分鐘前"
                media={
                  <div className="w-8 h-8 rounded-lg bg-[#007AFF]/10 flex items-center justify-center">
                    <Gamepad2 className="w-4 h-4 text-[#007AFF]" />
                  </div>
                }
              />
              <ListItem
                title="存款 $500.00"
                subtitle="2 小時前"
                media={
                  <div className="w-8 h-8 rounded-lg bg-[#34C759]/10 flex items-center justify-center">
                    <ArrowDownToLine className="w-4 h-4 text-[#34C759]" />
                  </div>
                }
              />
              <ListItem
                title="贏得底池 $120.00"
                subtitle="3 小時前"
                media={
                  <div className="w-8 h-8 rounded-lg bg-[#FF9500]/10 flex items-center justify-center">
                    <Wallet className="w-4 h-4 text-[#FF9500]" />
                  </div>
                }
              />
            </List>

            {/* Actions */}
            <Block className="!px-4 !mb-6 space-y-3">
              {selectedUser.status === 'banned' ? (
                <Button large rounded onClick={handleBanToggle}>
                  <span className="flex items-center gap-2">
                    <UserCheck className="w-5 h-5" />
                    解除停權
                  </span>
                </Button>
              ) : (
                <Button
                  large
                  rounded
                  onClick={handleBanToggle}
                  colors={{
                    fillBgIos: 'bg-[#FF3B30] active:bg-[#D32F2F]',
                    fillTextIos: 'text-white',
                  }}
                >
                  <span className="flex items-center gap-2">
                    <Ban className="w-5 h-5" />
                    停權帳號
                  </span>
                </Button>
              )}
            </Block>
          </div>
        )}
      </Sheet>

      {/* Ban/Unban Confirmation Dialog */}
      <Dialog
        opened={banDialogOpen}
        onBackdropClick={() => setBanDialogOpen(false)}
        title={
          selectedUser?.status === 'banned' ? '確認解除停權' : '確認停權'
        }
        content={
          selectedUser?.status === 'banned'
            ? `確定要解除 ${selectedUser?.name} 的停權狀態嗎？該用戶將可以重新登入並進行遊戲。`
            : `確定要停權 ${selectedUser?.name} 嗎？該用戶將無法登入或進行任何遊戲操作。`
        }
        buttons={
          <>
            <DialogButton onClick={() => setBanDialogOpen(false)}>
              取消
            </DialogButton>
            <DialogButton strong onClick={handleBanConfirm}>
              確認
            </DialogButton>
          </>
        }
      />
    </div>
  )
}
